/* eslint-disable no-unused-vars */
import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { submitBrandSize } from '../api'
import './SizeModal.css'

const SIZES = ['XS', 'S', 'M', 'L', 'XL']

const blockInvalidKeys = (e) => {
    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault()
}

function SizeModal({ brand, existingSize, onClose, onSaved }) {
    const { user } = useAuth()

    const [shirtSize, setShirtSize] = useState(existingSize?.shirt_size || '')
    const [outerwearSize, setOuterwearSize] = useState(existingSize?.outerwear_size || '')
    const [bottomsSize, setBottomsSize] = useState(existingSize?.bottoms_size || '')
    const [pantsWaist, setPantsWaist] = useState(existingSize?.pants_waist || '')
    const [pantsInseam, setPantsInseam] = useState(existingSize?.pants_inseam || '')
    const [shoeSize, setShoeSize] = useState(existingSize?.shoe_size || '')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')

    const validate = () => {
        if (pantsWaist && (isNaN(pantsWaist) || pantsWaist < 20 || pantsWaist > 50))
            return 'Pants waist must be between 20 and 50'
        if (pantsInseam && (isNaN(pantsInseam) || pantsInseam < 20 || pantsInseam > 50))
            return 'Pants inseam must be between 20 and 50'
        if (shoeSize && (isNaN(shoeSize) || shoeSize < 1 || shoeSize > 20))
            return 'Shoe size must be between 1 and 20'
        if (pantsWaist && !pantsInseam) return 'Please enter both waist and inseam'
        if (pantsInseam && !pantsWaist) return 'Please enter both waist and inseam'
        return ''
    }

    const hasAnySize = shirtSize || outerwearSize || bottomsSize || (pantsWaist && pantsInseam) || shoeSize

    const handleSave = async () => {
        const validationError = validate()
        if (validationError) { setError(validationError); return }
        if (!hasAnySize) { setError('Please enter at least one size'); return }
        setSaving(true)
        setError('')
        try {
            await submitBrandSize(user.id, brand.id, {
                shirt_size: shirtSize || null,
                outerwear_size: outerwearSize || null,
                bottoms_size: bottomsSize || null,
                pants_waist: pantsWaist ? parseInt(pantsWaist) : null,
                pants_inseam: pantsInseam ? parseInt(pantsInseam) : null,
                shoe_size: shoeSize ? parseFloat(shoeSize) : null,
            })
            onSaved && onSaved()
            onClose()
        } catch (e) {
            setError('Failed to save. Please try again.')
        }
        setSaving(false)
    }

    const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose() }

    const SizePills = ({ value, onChange }) => (
        <div className="size-pills">
            {SIZES.map(s => (
                <button
                    key={s}
                    className={`size-pill ${value === s ? 'active' : ''}`}
                    onClick={() => onChange(value === s ? '' : s)}
                >{s}</button>
            ))}
        </div>
    )

    return (
        <div className="size-modal-backdrop" onClick={handleBackdrop}>
            <div className="size-modal">
                <div className="size-modal-header">
                    <div>
                        <h3 className="size-modal-title">My sizing at {brand.name}</h3>
                        <p className="size-modal-subtitle">Leave blank for categories you don't own.</p>
                    </div>
                    <button className="size-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="size-modal-body">
                    <div className="size-field-group">
                        <label className="size-field-label">Shirts</label>
                        <SizePills value={shirtSize} onChange={setShirtSize} />
                    </div>
                    <div className="size-field-group">
                        <label className="size-field-label">Outerwear & Knitwear</label>
                        <SizePills value={outerwearSize} onChange={setOuterwearSize} />
                    </div>
                    <div className="size-field-group">
                        <label className="size-field-label">Bottoms <span className="size-field-hint">(shorts, sweatpants)</span></label>
                        <SizePills value={bottomsSize} onChange={setBottomsSize} />
                    </div>
                    <div className="size-field-group">
                        <label className="size-field-label">Pants</label>
                        <div className="size-pants-inputs">
                            <div className="size-input-wrapper">
                                <input type="number" className="size-input" placeholder="Waist"
                                    value={pantsWaist} min={20} max={50}
                                    onKeyDown={blockInvalidKeys}
                                    onChange={e => setPantsWaist(e.target.value)} />
                                <span className="size-input-unit">W</span>
                            </div>
                            <span className="size-pants-sep">×</span>
                            <div className="size-input-wrapper">
                                <input type="number" className="size-input" placeholder="Inseam"
                                    value={pantsInseam} min={20} max={50}
                                    onKeyDown={blockInvalidKeys}
                                    onChange={e => setPantsInseam(e.target.value)} />
                                <span className="size-input-unit">L</span>
                            </div>
                        </div>
                    </div>
                    <div className="size-field-group">
                        <label className="size-field-label">Shoes <span className="size-field-hint">(US)</span></label>
                        <div className="size-input-wrapper" style={{ width: '120px' }}>
                            <input type="number" className="size-input" placeholder="e.g. 10"
                                value={shoeSize} min={1} max={20} step={0.5}
                                onKeyDown={blockInvalidKeys}
                                onChange={e => setShoeSize(e.target.value)} />
                        </div>
                    </div>
                </div>

                {error && <p className="size-modal-error">{error}</p>}

                <div className="size-modal-footer">
                    <button className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save sizing'}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default SizeModal
